import type { OptionRow } from '../services/gexCalculator';
import type { IntradayVolRegime, VolAnalytics } from '../types/marketAnalytics';

function normIv(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n < 1) return n * 100;
  if (n > 300) return n / 100;
  return n;
}

function isCall(row: OptionRow): boolean {
  return String(row['Option Type'] ?? row.optionType ?? '').toUpperCase().startsWith('C');
}

function parseDte(row: OptionRow): number | null {
  const n = Number(row.DTE ?? row.dte ?? row['Days To Expiration'] ?? NaN);
  if (Number.isFinite(n) && n >= 0) return n;
  return null;
}

function intradayRegime(minutesToClose: number): IntradayVolRegime {
  if (minutesToClose <= 0) return 'closed';
  if (minutesToClose > 180) return 'morning';
  if (minutesToClose > 90) return 'midday';
  return 'power_hour';
}

/** Vol surface + HAR-style IV forecast + short VRP proxy (module 2). */
export function buildVolAnalytics(rows: OptionRow[], spot: number, minutesToClose: number): VolAnalytics {
  const empty: VolAnalytics = {
    atm_iv: null,
    otm_put_iv: null,
    otm_call_iv: null,
    surface_curvature: null,
    intraday_regime: intradayRegime(minutesToClose),
    short_vrp: null,
    iv_har_forecast: null,
  };
  if (!rows.length || spot <= 0) return empty;

  const byStrike = new Map<number, { c?: number; p?: number }>();
  const dteIv: { dte: number; iv: number }[] = [];

  for (const row of rows) {
    const k = Number(row.Strike ?? row.strike ?? 0);
    const iv = normIv(row['Implied Volatility'] ?? row.iv);
    if (!Number.isFinite(k) || iv <= 0) continue;
    const side = isCall(row) ? 'c' : 'p';
    const cur = byStrike.get(k) || {};
    if (side === 'c') cur.c = iv;
    else cur.p = iv;
    byStrike.set(k, cur);
    const dte = parseDte(row);
    if (dte != null) dteIv.push({ dte, iv });
  }

  let atmIv: number | null = null;
  let otmPut: number | null = null;
  let otmCall: number | null = null;
  if (byStrike.size) {
    let bestK = 0;
    let bestD = Infinity;
    for (const k of Array.from(byStrike.keys())) {
      const d = Math.abs(k - spot);
      if (d < bestD) {
        bestD = d;
        bestK = k;
      }
    }
    const atm = byStrike.get(bestK);
    if (atm) {
      const vals = [atm.c, atm.p].filter((v): v is number => v != null && v > 0);
      if (vals.length) atmIv = vals.reduce((a, b) => a + b, 0) / vals.length;
    }
    let putK = 0;
    let callK = 0;
    let putD = Infinity;
    let callD = Infinity;
    for (const k of Array.from(byStrike.keys())) {
      if (k < spot * 0.98) {
        const d = spot - k;
        if (d < putD) {
          putD = d;
          putK = k;
        }
      }
      if (k > spot * 1.02) {
        const d = k - spot;
        if (d < callD) {
          callD = d;
          callK = k;
        }
      }
    }
    otmPut = byStrike.get(putK)?.p ?? null;
    otmCall = byStrike.get(callK)?.c ?? null;
  }

  let shortIv = 0;
  let midIv = 0;
  let longIv = 0;
  let sn = 0;
  let mn = 0;
  let ln = 0;
  for (const { dte, iv } of dteIv) {
    if (dte <= 4) {
      shortIv += iv;
      sn += 1;
    } else if (dte <= 21) {
      midIv += iv;
      mn += 1;
    } else {
      longIv += iv;
      ln += 1;
    }
  }
  const s = sn ? shortIv / sn : atmIv;
  const m = mn ? midIv / mn : atmIv;
  const l = ln ? longIv / ln : atmIv;
  const iv_har_forecast =
    s != null && m != null && l != null ? 0.5 * (s ?? 0) + 0.3 * (m ?? 0) + 0.2 * (l ?? 0) : atmIv;

  const rvProxy = atmIv != null ? atmIv * 0.85 : null;
  const short_vrp =
    atmIv != null && rvProxy != null ? (atmIv / 100) ** 2 - (rvProxy / 100) ** 2 : null;

  const curvature =
    atmIv != null && otmPut != null ? otmPut - atmIv : null;

  return {
    atm_iv: atmIv,
    otm_put_iv: otmPut,
    otm_call_iv: otmCall,
    surface_curvature: curvature,
    intraday_regime: intradayRegime(minutesToClose),
    short_vrp,
    iv_har_forecast,
  };
}
